export type TransferStep = 1 | 2 | 3;

export type TransferView = {
  step: TransferStep;
  // Number of consecutive earlier transfer steps created by this flow.
  depth: number;
  amountOpen: boolean;
};

const HISTORY_KEY = "__julyupayTransferFlow";

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown> : {};
}

export function readTransferView(state: unknown, scope: string): TransferView {
  const saved = record(record(state)[HISTORY_KEY]);
  if (saved.scope !== scope || ![1, 2, 3].includes(Number(saved.step)) || typeof saved.step !== "number") {
    return { step: 1, depth: 0, amountOpen: false };
  }
  const step = saved.step as TransferStep;
  const depth = Number.isInteger(saved.depth) && Number(saved.depth) >= 0 && Number(saved.depth) < step
    ? Number(saved.depth) : 0;
  return { step, depth, amountOpen: step === 2 && saved.amountOpen === true };
}

export function transferViewState(state: unknown, scope: string, view: TransferView) {
  return { ...record(state), [HISTORY_KEY]: { ...view, scope } };
}

export function nextTransferView(view: TransferView): TransferView {
  if (view.step === 3 || view.amountOpen) return view;
  return { step: (view.step + 1) as TransferStep, depth: view.depth + 1, amountOpen: false };
}

/** Only pop entries that this flow actually created; direct links use replacement. */
export function transferBackDelta(view: TransferView, target: TransferStep): number | null {
  const stepsBack = view.step - target;
  if (stepsBack < 0 || stepsBack > view.depth) return null;
  const delta = stepsBack + Number(view.amountOpen);
  return delta > 0 ? -delta : null;
}

export function allowedTransferStep(requested: TransferStep, verified: boolean, canSubmit: boolean): TransferStep {
  if (!verified) return 1;
  return requested === 3 && !canSubmit ? 2 : requested;
}

export function transferStepTitle(step: TransferStep, hasEntryAmount: boolean): string {
  if (step === 1) return "누구에게 보낼까요?";
  if (step === 3) return "신청 내용을 확인해 주세요";
  return hasEntryAmount ? "송금 일정·분할결제" : "얼마를 보낼까요?";
}

export function transferDocumentPath(category: string, sessionKey: string, amountEntry: boolean): string {
  const params = new URLSearchParams({ category, session_key: sessionKey });
  if (amountEntry) params.set("entry", "amount");
  return `/contracts/new?${params}`;
}

export function hasTransferDocumentHistory(state: unknown, category: string, sessionKey: string): boolean {
  const from = record(state).julyupayTransferFrom;
  if (typeof from !== "string" || !from.startsWith("/contracts/new?")) return false;
  // Fragments are not query parameters, including when a document anchor contains '&'.
  const url = new URL(from, "https://julyupay.invalid");
  return url.pathname === "/contracts/new"
    && (url.searchParams.get("category") || "drink") === category
    && url.searchParams.get("session_key") === sessionKey;
}
